import { DATA, DATA_LABEL } from '../../theme/type.js';
import { formatClock } from '../model/countdown.js';
import type { MwTrackRow } from '../model/mw.js';

/**
 * The timeline dock (design 837–880).
 *
 * Every track's four stages on one shared axis: the orders window. Not each
 * track's own span — two lanes drawn at different speeds would be a chart of
 * nothing — so a lane whose marks bunch at the left is a launch whose data all
 * arrives early, and that is a fact about the window the operator can read at a
 * glance.
 *
 * The **now** line is the one thing on this screen that moves on its own, and it
 * is placed from `serverNowMs` — corrected server time, passed in. There is no
 * clock in this file and nothing here can reach one.
 *
 * A mark is `OBSERVED` or `PENDING`, and a pending mark with no scheduled time is
 * not drawn at all: a briefed track has an empty `stageSchedule` because it
 * arrived named and dismissed, and drawing four grey ticks across its lane would
 * say the data is still coming.
 */

export interface TimelineDockProps {
  readonly rows: readonly MwTrackRow[];
  readonly span: { readonly fromMs: number; readonly toMs: number } | null;
  readonly serverNowMs: number;
  readonly selected: string | null;
  readonly onSelect: (eventId: string) => void;
}

const TIMELINE_HEIGHT = 122;

/** Wide enough for a full event id: a lane whose name is cut off is a lane nobody uses. */
const LANE_LABEL_WIDTH = 124;
/**
 * The label's own left inset.
 *
 * It does **not** widen the column: `tokens.css` sets `box-sizing: border-box`
 * on everything under `[data-bs-theme]`, so the padding is inside the 124. That
 * matters here and nowhere else in this file, because the axis begins where the
 * label's border box ends.
 */
const LANE_LABEL_INSET = 12;
/** Between the label column and the axis. */
const LANE_GAP = 8;
/** The lane's right padding, which the axis stops short of. */
const LANE_PAD_RIGHT = 12;

/**
 * Where the shared axis begins, and how much of the lane it does not get.
 *
 * These exist as constants because the **now** line and the stage marks have to
 * be placed against the same span, and they are not siblings — the marks live
 * inside each lane's axis element, the line is drawn once across every lane. It
 * previously took its percentage from the scroll container and added
 * `LANE_LABEL_WIDTH` back, which is a different basis: the line then ran
 * `156px` further than the axis over a window, measuring −8 px at the open and
 * **+136 px** at the deadline, and left the dock entirely for the last 35 s of
 * every window. A mark and the line that reads against it must divide the same
 * number.
 */
const AXIS_LEFT = LANE_LABEL_WIDTH + LANE_GAP;
const AXIS_INSET = AXIS_LEFT + LANE_PAD_RIGHT;

export function TimelineDock(props: TimelineDockProps): JSX.Element | null {
  const span = props.span;
  if (span === null || props.rows.length === 0) return null;

  const width = Math.max(1, span.toMs - span.fromMs);
  const now = Math.max(0, Math.min(1, (props.serverNowMs - span.fromMs) / width));
  const remaining = Math.max(0, span.toMs - props.serverNowMs);

  return (
    <div
      data-testid="mw-timeline"
      style={{
        flex: 'none',
        height: TIMELINE_HEIGHT,
        display: 'flex',
        flexDirection: 'column',
        background: 'var(--surface)',
        borderTop: '1px solid var(--border-strong)',
      }}
    >
      <div
        style={{
          flex: 'none',
          display: 'flex',
          alignItems: 'center',
          gap: 14,
          padding: '5px 12px',
          borderBottom: '1px solid var(--rule)',
        }}
      >
        {/* The key draws the mark it names. Cyan, because the marks are cyan and
            a legend in a colour absent from its own chart is worse than none —
            and because a stage that has landed is a reading, not an act. */}
        <Key colour="var(--accent)" filled label="STAGE OBSERVED" />
        <Key colour="var(--border-strong)" label="PENDING" />
        <span style={{ flex: 1 }} />
        <span data-testid="mw-window-left" style={{ ...DATA, color: 'var(--text-2)' }}>
          WINDOW CLOSES {formatClock(remaining)}
        </span>
      </div>

      <div style={{ flex: 1, minHeight: 0, position: 'relative', overflowY: 'auto' }}>
        {/* One line, drawn over every lane, because the whole point is that the
            lanes share an axis. */}
        <div
          data-testid="mw-timeline-now"
          style={{
            position: 'absolute',
            top: 0,
            bottom: 0,
            // The axis's own span, not the container's: `AXIS_LEFT` in, and
            // `AXIS_INSET` narrower than the lane.
            left:
              'calc(' +
              String(AXIS_LEFT) +
              'px + (100% - ' +
              String(AXIS_INSET) +
              'px) * ' +
              String(Math.round(now * 1000) / 1000) +
              ')',
            width: 1,
            background: 'var(--red)',
            pointerEvents: 'none',
          }}
        />
        {props.rows.map((row) => (
          <button
            key={row.eventId}
            type="button"
            data-testid="mw-lane"
            data-event={row.eventId}
            onClick={() => props.onSelect(row.eventId)}
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              gap: LANE_GAP,
              height: 22,
              padding: '0 ' + String(LANE_PAD_RIGHT) + 'px 0 0',
              background: row.eventId === props.selected ? 'var(--surface-3)' : 'transparent',
              border: 0,
              textAlign: 'left',
              cursor: 'pointer',
            }}
          >
            <span
              style={{
                ...DATA,
                flex: 'none',
                width: LANE_LABEL_WIDTH,
                paddingLeft: LANE_LABEL_INSET,
                fontWeight: 400,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                color: 'var(--text-muted)',
              }}
            >
              {row.eventId}
            </span>
            <span
              style={{
                flex: 1,
                minWidth: 0,
                position: 'relative',
                height: 2,
                background: 'var(--rule)',
              }}
            >
              {row.pips.map((pip) =>
                // A stage with no scheduled instant has nowhere to sit on a shared
                // axis, and a mark placed anyway would be a claim about when.
                pip.at === null ? null : (
                  <span
                    key={pip.stage}
                    data-testid="mw-lane-mark"
                    data-stage={pip.stage}
                    data-pip={pip.state}
                    style={{
                      position: 'absolute',
                      top: -3,
                      left: 'calc(' + String(Math.round(pip.at * 1000) / 10) + '% - 4px)',
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      background: pip.state === 'OBSERVED' ? 'var(--accent)' : 'var(--surface)',
                      border:
                        '1px solid ' +
                        (pip.state === 'OBSERVED' ? 'var(--accent)' : 'var(--border-strong)'),
                    }}
                  />
                ),
              )}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

function Key(props: {
  readonly colour: string;
  readonly filled?: boolean;
  readonly label: string;
}): JSX.Element {
  return (
    <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
      <span
        style={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          background: props.filled === true ? props.colour : 'transparent',
          border: '1px solid ' + props.colour,
        }}
      />
      <span style={{ ...DATA_LABEL, fontSize: 10, fontWeight: 400, color: 'var(--text-muted)' }}>
        {props.label}
      </span>
    </span>
  );
}
